import { DatePicker, Label } from "@tellescope/ui";
import { TellescopeFormFieldBaseProps } from "../types";
import { cn, formatDate } from "@tellescope/utils";
import { useMemo } from "react";
import { FieldDescription } from "../FieldDescription";
import { useMaskedDateInput } from "./useMaskedDateInput";
import { MaskedDateHelperText } from "./MaskedDateHelperText";

export interface DateStringFieldProps extends TellescopeFormFieldBaseProps<"dateString"> {}

export const DateStringField = ({
  title,
  description,
  htmlDescription,
  placeholder,
  value,
  onChange,
  className,
  error,
  onBlur,
  required,
  options,
  autoFocus,
}: DateStringFieldProps) => {
  const dateFromValue = useMemo(() => {
    if (!value) return undefined;
    const splits = value.split("-");
    const monthStr = splits[0];
    if (monthStr === undefined) return undefined;
    const month = parseInt(monthStr);
    const dateStr = splits[1];
    if (dateStr === undefined) return undefined;
    const date = parseInt(dateStr);
    const yearStr = splits[2];
    if (yearStr === undefined) return undefined;
    const year = parseInt(yearStr);
    return new Date(year, month - 1, date);
  }, [value]);

  const now = Date.now();
  const minDate = options?.minDateOffsetMs !== undefined ? new Date(now + options.minDateOffsetMs) : undefined;
  const maxDate = options?.maxDateOffsetMs !== undefined ? new Date(now + options.maxDateOffsetMs) : undefined;
  const disabledDates = [...(minDate ? [{ before: minDate }] : []), ...(maxDate ? [{ after: maxDate }] : [])];

  const {
    value: maskedValue,
    parsed,
    ...maskedInputProps
  } = useMaskedDateInput({
    maskedValue: value ?? "",
    onCommit: (date) => onChange(date ? formatDate(date, "MM-DD-YYYY") : undefined),
  });

  return (
    <div className="space-y-1">
      {!!(title || placeholder) && <Label required={required}>{title || placeholder}</Label>}
      <FieldDescription description={description} htmlDescription={htmlDescription} />
      <DatePicker
        value={dateFromValue}
        onChange={(date) => {
          onChange(date ? formatDate(date, "MM-DD-YYYY") : undefined);
        }}
        onBlur={onBlur}
        onClose={onBlur}
        className={cn(className, !!error && "border-destructive")}
        displayFormat="MM-DD-YYYY"
        placeholder={placeholder ?? "MM-DD-YYYY"}
        label=""
        disabledDates={disabledDates.length > 0 ? disabledDates : undefined}
        inputProps={{ autoFocus, value: maskedValue, ...maskedInputProps }}
      />
      <MaskedDateHelperText value={parsed} />
      {!!error && <p className="text-sm text-destructive mt-1">{error}</p>}
    </div>
  );
};
